// Import shared enums from exercises
import { DifficultyLevel, getDifficultyLevelName } from '../platformExercises/types';

// Block Category enum matching backend
export enum BlockCategory {
  WarmUp = 1,
  Footwork = 2,
  Abdominal = 3,
  HipWork = 4,
  ArmWork = 5,
  BackWork = 6,
  Stretching = 7,
  CoolDown = 8,
  CircuitTraining = 9,
  CardioSequence = 10
}

// Block Scope enum matching backend
export enum BlockScope {
  Instructor = 1,  // Only visible to creator
  Studio = 2,      // Visible to all instructors in studio
  Global = 3       // Platform-wide visibility
}

// Helper functions for display names
export const getBlockCategoryName = (category: BlockCategory): string => {
  switch (category) {
    case BlockCategory.WarmUp:
      return 'Warm Up';
    case BlockCategory.Footwork:
      return 'Footwork';
    case BlockCategory.Abdominal:
      return 'Abdominal';
    case BlockCategory.HipWork:
      return 'Hip Work';
    case BlockCategory.ArmWork:
      return 'Arm Work';
    case BlockCategory.BackWork:
      return 'Back Work';
    case BlockCategory.Stretching:
      return 'Stretching';
    case BlockCategory.CoolDown:
      return 'Cool Down';
    case BlockCategory.CircuitTraining:
      return 'Circuit Training';
    case BlockCategory.CardioSequence:
      return 'Cardio Sequence';
    default:
      return 'Unknown';
  }
};

export const getBlockScopeName = (scope: BlockScope): string => {
  switch (scope) {
    case BlockScope.Instructor:
      return 'Instructor';
    case BlockScope.Studio:
      return 'Studio';
    case BlockScope.Global:
      return 'Global';
    default:
      return 'Unknown';
  }
};

// Re-export DifficultyLevel and helper for consistency
export { DifficultyLevel, getDifficultyLevelName };

// Exercise in block definition
export interface CreateBlockExerciseRequest {
  exerciseId: number;
  orderIndex: number;
  durationSeconds?: number;
  repetitions?: number;
  springSettings?: string;
  equipmentSetupId?: number;
  notes?: string;
  transitionSeconds: number;
  isOptional: boolean;
}

export interface BlockExerciseDetailResponse {
  id: number;
  exerciseId: number;
  exerciseName: string;
  orderIndex: number;
  durationSeconds?: number;
  repetitions?: number;
  springSettings?: string;
  equipmentSetupId?: number;
  notes?: string;
  transitionSeconds: number;
  isOptional: boolean;
}

// API Request/Response Types
export interface CreatePlatformBlockRequest {
  name: string;
  description?: string;
  category: BlockCategory;
  difficulty: DifficultyLevel;
  durationMinutes: number;
  primaryEquipmentId?: number;
  scope: BlockScope;
  displayOrder: number;
  tags?: string;
  originalAuthor?: string;
  exercises?: CreateBlockExerciseRequest[];
}

export interface UpdatePlatformBlockRequest {
  name?: string;
  description?: string;
  category: BlockCategory;
  difficulty: DifficultyLevel;
  durationMinutes: number;
  primaryEquipmentId?: number;
  scope: BlockScope;
  displayOrder: number;
  tags?: string;
  originalAuthor?: string;
  isActive: boolean;
  exercises?: CreateBlockExerciseRequest[];
}

export interface PlatformBlockSummaryResponse {
  id: number;
  name: string;
  description?: string;
  category: BlockCategory;
  difficulty: DifficultyLevel;
  durationMinutes: number;
  primaryEquipmentName?: string;
  exerciseCount: number;
  isActive: boolean;
  displayOrder: number;
  tags?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface PlatformBlockDetailResponse {
  id: number;
  name: string;
  description?: string;
  category: BlockCategory;
  difficulty: DifficultyLevel;
  durationMinutes: number;
  primaryEquipmentId?: number;
  primaryEquipmentName?: string;
  scope: BlockScope;
  displayOrder: number;
  isActive: boolean;
  tags?: string;
  createdBy?: string;
  originalAuthor?: string;
  isPlatformBlock: boolean;
  createdAt: string;
  updatedAt?: string;
  exercises: BlockExerciseDetailResponse[];
}

// Paginated API response
export interface PlatformBlockListResponse {
  blocks: PlatformBlockSummaryResponse[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

// Form data type for the block form
export interface BlockFormData {
  name: string;
  description: string;
  category: BlockCategory;
  difficulty: DifficultyLevel;
  durationMinutes: number;
  primaryEquipmentId?: number;
  scope: BlockScope;
  displayOrder: number;
  tags: string;
  originalAuthor: string;
  isActive?: boolean;
  exercises: BlockExerciseFormData[];
}

// Exercise form data within block
export interface BlockExerciseFormData {
  exerciseId: number;
  exerciseName?: string; // For display purposes
  orderIndex: number;
  durationSeconds?: number;
  repetitions?: number;
  springSettings: string;
  equipmentSetupId?: number;
  notes: string;
  transitionSeconds: number;
  isOptional: boolean;
}

// Exercise option for dropdowns
export interface ExerciseOption {
  id: number;
  name: string;
  category: string;
  difficulty: string;
  primaryEquipmentName: string;
  typicalDurationSeconds?: number;
  defaultReps?: number;
}

// Equipment option for dropdowns
export interface EquipmentOption {
  id: number;
  name: string;
  category: string;
  requiresSetup: boolean;
}